import { useEffect, useState } from "react";
import { api } from "../lib/api";
import { X, MessageCircle, Timer } from "lucide-react";
import { OfferContent } from "../pages/AdminOffers";

/* Public promo popup — appears on every visit while the current active offer is live.
   Visitors can close with X (no persistence per user choice) or tap the CTA, which
   triggers onRequestInfo(offerTitle) to open the AI quote dialog pre-filled. */
export default function OfferPopup({ onRequestInfo }) {
  const [offer, setOffer] = useState(null);
  const [open, setOpen] = useState(false);
  const [daysLeft, setDaysLeft] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const r = await api.get("/offers/public/active");
        if (r.data && r.data.offer_id) {
          setOffer(r.data);
          setOpen(true);
          if (r.data.valid_until) {
            const diff = Math.ceil((new Date(r.data.valid_until) - new Date()) / (1000 * 60 * 60 * 24));
            setDaysLeft(Math.max(0, diff));
          }
        }
      } catch { /* silent — no offer is fine */ }
    })();
  }, []);

  if (!offer || !open) return null;

  const close = () => setOpen(false);
  const requestInfo = () => {
    setOpen(false);
    onRequestInfo && onRequestInfo(offer.title);
  };

  return (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4"
         role="dialog" aria-modal="true" data-testid="public-offer-popup">
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={close}/>
      <div className="relative bg-white rounded-2xl shadow-2xl max-w-lg w-full overflow-hidden animate-in zoom-in-95 duration-300"
           onClick={e => e.stopPropagation()}>
        <button onClick={close}
                className="absolute top-3 right-3 z-20 w-9 h-9 rounded-full bg-black/50 hover:bg-black/70 text-white flex items-center justify-center backdrop-blur-sm transition"
                aria-label="Chiudi" data-testid="offer-close-btn">
          <X className="w-5 h-5"/>
        </button>

        <OfferContent offer={offer}/>

        {daysLeft !== null && daysLeft <= 30 && (
          <div className="px-4 pt-3 flex items-center justify-center gap-1.5 text-xs text-amber-700 font-semibold">
            <Timer className="w-3.5 h-3.5"/>
            {daysLeft === 0 ? "Ultimo giorno!" : daysLeft === 1 ? "Scade domani!" : `Ancora ${daysLeft} giorni`}
          </div>
        )}

        <div className="p-4 bg-slate-50 flex gap-2 border-t border-slate-200">
          <button onClick={close}
                  className="flex-1 py-3 rounded-lg border border-slate-300 bg-white hover:bg-slate-100 text-slate-700 font-medium transition"
                  data-testid="offer-later-btn">
            Guarda più tardi
          </button>
          <button onClick={requestInfo}
                  className="flex-[2] py-3 rounded-lg bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600 text-white font-semibold shadow-lg transition flex items-center justify-center gap-2"
                  data-testid="offer-cta-btn">
            <MessageCircle className="w-4 h-4"/>
            {offer.cta_label || "Richiedi informazioni"}
          </button>
        </div>
      </div>
    </div>
  );
}
